// Rules for VALIDATION_CONSTRAINTS.md section 13 — graph reachability.
//
// Reachability uses non-recursive sequence flows (`getFlowOutgoing`). Recursive
// edges (edge.data.isRecursive) are engine annotations overlaying real edges —
// they MUST NOT drive reachability (they form cycles that confuse DFS). Rules
// 13.6/13.7 need to see recursive edges and therefore use raw `getOutgoing`/
// `getIncoming`.
import { registerRule } from '../registry';
import { ValidationRule, ValidationIssue } from '../types';
import {
  buildLookup,
  getFlowOutgoing,
  getFlowIncoming,
  getOutgoing,
  getFamily,
  isBoundaryEvent,
  isStartEvent,
  isEndEvent,
  labelOf,
  getNode,
} from '../helpers';
import { ProcessLookup } from '../types';
import { ProcessDefinition } from '../../../types';

/** BFS forward from a set of seeds using non-recursive sequence flows only.
 *  Also follows boundary-event attachments: when a host activity is visited,
 *  every boundary event attached to it (parentNode === host) is reachable
 *  too, since boundaries fire based on host execution, not a sequence flow.
 */
const bfsForward = (
  lookup: ProcessLookup,
  seeds: string[],
  boundariesByHost?: Map<string, string[]>
): Set<string> => {
  const visited = new Set<string>();
  const queue: string[] = [];
  const push = (id: string) => {
    if (!visited.has(id)) {
      visited.add(id);
      queue.push(id);
    }
  };
  seeds.forEach(push);
  while (queue.length) {
    const id = queue.shift()!;
    for (const e of getFlowOutgoing(lookup, id)) push(e.target);
    if (boundariesByHost) {
      const attached = boundariesByHost.get(id);
      if (attached) attached.forEach(push);
    }
  }
  return visited;
};

const collectReachable = (process: ProcessDefinition, lookup: ProcessLookup): Set<string> => {
  const starts = process.nodes.filter(n => isStartEvent(n)).map(n => n.id);
  const boundariesByHost = new Map<string, string[]>();
  for (const n of process.nodes) {
    const host = (n as any).parentNode as string | undefined;
    if (host && n.type?.endsWith('BoundaryEvent')) {
      const list = boundariesByHost.get(host) || [];
      list.push(n.id);
      boundariesByHost.set(host, list);
    }
  }
  return bfsForward(lookup, starts, boundariesByHost);
};

const rule_13_1_ReachableFromStart: ValidationRule = {
  id: '13.1',
  description: 'Every non-boundary node must be reachable from some start event',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const reachable = collectReachable(process, lookup);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (isBoundaryEvent(n)) continue;
      if (!reachable.has(n.id)) {
        issues.push({
          severity: 'error',
          rule: '13.1',
          message: `${labelOf(n)} is not reachable from any start event.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_13_1_ReachableFromStart);

const rule_13_2_BoundaryReachableViaHost: ValidationRule = {
  id: '13.2',
  description: 'Every boundary event must attach to an activity that is itself reachable',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const reachable = collectReachable(process, lookup);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (!isBoundaryEvent(n)) continue;
      const parentId = (n as any).parentNode as string | undefined;
      if (!parentId) {
        issues.push({
          severity: 'error',
          rule: '13.2',
          message: `${labelOf(n)} is a boundary event with no host activity (parentNode missing).`,
          nodeIds: [n.id],
        });
        continue;
      }
      const host = getNode(lookup, parentId);
      if (!host) {
        // Missing host is surfaced by 1.4; still emit a reachability error.
        issues.push({
          severity: 'error',
          rule: '13.2',
          message: `${labelOf(n)} references missing host activity "${parentId}".`,
          nodeIds: [n.id],
        });
        continue;
      }
      if (!reachable.has(host.id)) {
        issues.push({
          severity: 'error',
          rule: '13.2',
          message: `${labelOf(n)} is attached to ${labelOf(host)} which is not reachable from any start event.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_13_2_BoundaryReachableViaHost);

const rule_13_3_StartReachesTerminal: ValidationRule = {
  id: '13.3',
  description: 'At least one start event must reach at least one EndEvent',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const starts = process.nodes.filter(n => isStartEvent(n));
    if (starts.length === 0) return []; // section 5 owns "≥1 start event"
    const reachable = bfsForward(lookup, starts.map(s => s.id));
    const anyTerminal = process.nodes.some(n => isEndEvent(n) && reachable.has(n.id));
    if (anyTerminal) return [];
    return [{
      severity: 'error',
      rule: '13.3',
      message: `No start event can reach any terminal (EndEvent) — process has no completion path.`,
      nodeIds: starts.map(s => s.id),
    }];
  },
};
registerRule(rule_13_3_StartReachesTerminal);

const rule_13_4_ZeroOutgoingMustBeEnd: ValidationRule = {
  id: '13.4',
  description: 'Nodes with zero outgoing sequence flows must be EndEvent subclasses',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      // Count ALL outgoing (including recursive) — a recursive-flagged
      // outgoing flow still means the node is not a dead end.
      if (getOutgoing(lookup, n.id).length !== 0) continue;
      if (getFamily(n.type) === 'EndEvent') continue;
      issues.push({
        severity: 'error',
        rule: '13.4',
        message: `${labelOf(n)} has no outgoing flows but is not an EndEvent.`,
        nodeIds: [n.id],
      });
    }
    return issues;
  },
};
registerRule(rule_13_4_ZeroOutgoingMustBeEnd);

const rule_13_5_ZeroIncomingMustBeStartOrBoundary: ValidationRule = {
  id: '13.5',
  description: 'Nodes with zero incoming sequence flows must be StartEvent or BoundaryEvent',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (getFlowIncoming(lookup, n.id).length !== 0) continue;
      const fam = getFamily(n.type);
      if (fam === 'StartEvent' || fam === 'BoundaryEvent') continue;
      issues.push({
        severity: 'error',
        rule: '13.5',
        message: `${labelOf(n)} has no incoming flows but is neither a StartEvent nor a BoundaryEvent.`,
        nodeIds: [n.id],
      });
    }
    return issues;
  },
};
registerRule(rule_13_5_ZeroIncomingMustBeStartOrBoundary);

const rule_13_6_BackEdgesMustBeRecursive: ValidationRule = {
  id: '13.6',
  description: 'DFS back-edges must be flagged as recursive (data.isRecursive)',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    // DFS from each start event (and any zero-incoming node as fallback root)
    // over ALL edges — we need to see candidate back-edges regardless of flag.
    const WHITE = 0, GRAY = 1, BLACK = 2;
    const color = new Map<string, number>();
    for (const n of process.nodes) color.set(n.id, WHITE);

    const roots: string[] = [];
    for (const n of process.nodes) {
      if (isStartEvent(n)) roots.push(n.id);
    }
    // Include any other white node as a DFS root so isolated cycles still examined.
    for (const n of process.nodes) {
      if (!roots.includes(n.id)) roots.push(n.id);
    }

    const reported = new Set<string>();

    const visit = (id: string) => {
      color.set(id, GRAY);
      for (const e of getOutgoing(lookup, id)) {
        const target = e.target;
        const c = color.get(target) ?? WHITE;
        if (c === GRAY) {
          // back-edge
          const isRecursive = !!(e as any).data?.isRecursive;
          if (!isRecursive && !reported.has(e.id)) {
            reported.add(e.id);
            issues.push({
              severity: 'error',
              rule: '13.6',
              message: `Edge ${e.id} (${e.source} → ${e.target}) forms a cycle but is not flagged as recursive.`,
              edgeIds: [e.id],
            });
          }
        } else if (c === WHITE) {
          visit(target);
        }
      }
      color.set(id, BLACK);
    };

    for (const root of roots) {
      if (color.get(root) === WHITE) visit(root);
    }
    return issues;
  },
};
registerRule(rule_13_6_BackEdgesMustBeRecursive);

const rule_13_7_RecursiveEdgeMustFormCycle: ValidationRule = {
  id: '13.7',
  description: 'Every recursive-flagged edge must actually close a cycle back to its source',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const issues: ValidationIssue[] = [];
    for (const e of process.edges) {
      if (!(e as any).data?.isRecursive) continue;
      // Forward DFS from target over non-recursive edges; must reach source.
      const visited = new Set<string>();
      const stack: string[] = [e.target];
      let reachedSource = false;
      while (stack.length) {
        const id = stack.pop()!;
        if (id === e.source) { reachedSource = true; break; }
        if (visited.has(id)) continue;
        visited.add(id);
        for (const out of getFlowOutgoing(lookup, id)) {
          if (!visited.has(out.target)) stack.push(out.target);
        }
      }
      if (!reachedSource) {
        issues.push({
          severity: 'error',
          rule: '13.7',
          message: `Edge ${e.id} (${e.source} → ${e.target}) is flagged recursive but does not form a cycle — no forward path from target back to source.`,
          edgeIds: [e.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_13_7_RecursiveEdgeMustFormCycle);

const rule_13_8_ReachableLeadsToCompletion: ValidationRule = {
  id: '13.8',
  description: 'Every reachable node should lead to a terminal or intentional wait',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const lookup = buildLookup(process);
    const reachable = collectReachable(process, lookup);
    const issues: ValidationIssue[] = [];

    // A node "completes" if its forward-closure contains an EndEvent, an
    // IntermediateCatching event, or a BoundaryEvent attached to a
    // downstream activity. Traversal follows every edge (recursive ones
    // included) because a loop that eventually escapes through a gateway
    // default path still reaches completion — a true livelock would have
    // no non-recursive escape at any downstream gateway. Computed
    // per-node with memoization.
    const cache = new Map<string, boolean>();

    // Index boundary events by host so we can ask "does activity X have a
    // boundary attached?" — a boundary on a reachable activity is a valid wait.
    const boundariesByHost = new Map<string, string[]>();
    for (const n of process.nodes) {
      if (!isBoundaryEvent(n)) continue;
      const host = (n as any).parentNode as string | undefined;
      if (!host) continue;
      const list = boundariesByHost.get(host) ?? [];
      list.push(n.id);
      boundariesByHost.set(host, list);
    }

    const isIntentionalWait = (nodeId: string): boolean => {
      const node = getNode(lookup, nodeId);
      if (!node) return false;
      if (getFamily(node.type) === 'IntermediateCatching') return true;
      // Boundary attached to this node means there is an intentional wait
      // at this activity (the boundary catches an event).
      if (boundariesByHost.has(nodeId)) return true;
      return false;
    };

    const completes = (start: string): boolean => {
      const cached = cache.get(start);
      if (cached !== undefined) return cached;

      const visited = new Set<string>();
      const stack: string[] = [start];
      let found = false;
      while (stack.length) {
        const id = stack.pop()!;
        if (visited.has(id)) continue;
        visited.add(id);
        const node = getNode(lookup, id);
        if (node && isEndEvent(node)) { found = true; break; }
        if (isIntentionalWait(id)) { found = true; break; }
        for (const e of getOutgoing(lookup, id)) {
          if (!visited.has(e.target)) stack.push(e.target);
        }
      }
      cache.set(start, found);
      return found;
    };

    for (const n of process.nodes) {
      if (!reachable.has(n.id)) continue;
      if (isBoundaryEvent(n)) continue;
      if (isEndEvent(n)) continue;
      if (!completes(n.id)) {
        issues.push({
          severity: 'warning',
          rule: '13.8',
          message: `${labelOf(n)} has no forward path to a terminal or intentional wait.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_13_8_ReachableLeadsToCompletion);
